import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, ArrowRight, CheckCircle, Calculator, Target } from 'lucide-react';

interface ROIResult {
  monthlyROI: number;
  yearlyROI: number;
  monthlyRevenue: number;
  yearlyRevenue: number;
  investment: number;
}

const SEOROICalculator = () => {
  const [monthlyTraffic, setMonthlyTraffic] = useState<string>('');
  const [conversionRate, setConversionRate] = useState<string>('1.5');
  const [averageOrderValue, setAverageOrderValue] = useState<string>('');
  const [targetImprovement, setTargetImprovement] = useState<string>('');
  const [result, setResult] = useState<ROIResult | null>(null);
  const [showForm, setShowForm] = useState(false);

  // GA4 Tracking
  useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'roi_calculator_view', {
        event_category: 'engagement',
        event_label: 'seo_roi_calculator'
      });
    }
  }, []);

  const calculateROI = () => {
    console.log('Calculate ROI button clicked');
    console.log('Form values:', {
      monthlyTraffic,
      conversionRate,
      averageOrderValue,
      targetImprovement
    });

    const traffic = parseFloat(monthlyTraffic) || 0;
    const cvr = parseFloat(conversionRate) || 1.5;
    const aov = parseFloat(averageOrderValue) || 0;
    const improvement = parseFloat(targetImprovement) || 0;

    console.log('Parsed values:', { traffic, cvr, aov, improvement });

    // Current monthly revenue
    const currentMonthlyRevenue = traffic * (cvr / 100) * aov;
    
    // Projected monthly revenue after SEO improvement
    const newTraffic = traffic * (1 + improvement / 100);
    const newMonthlyRevenue = newTraffic * (cvr / 100) * aov;
    
    // Revenue increase
    const monthlyIncrease = newMonthlyRevenue - currentMonthlyRevenue;
    const yearlyIncrease = monthlyIncrease * 12;
    
    // Estimated SEO investment (based on Swiss market rates)
    const estimatedInvestment = Math.max(2000, Math.min(8000, traffic * 0.5));
    
    // ROI calculation
    const monthlyROI = estimatedInvestment > 0 ? (monthlyIncrease / estimatedInvestment) * 100 : 0;
    const yearlyROI = estimatedInvestment > 0 ? (yearlyIncrease / estimatedInvestment) * 100 : 0;

    const calculatedResult = {
      monthlyROI,
      yearlyROI,
      monthlyRevenue: monthlyIncrease,
      yearlyRevenue: yearlyIncrease,
      investment: estimatedInvestment
    };

    console.log('Calculated result:', calculatedResult);

    setResult(calculatedResult);

    // GA4 Tracking
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'roi_calculator_submit', {
        event_category: 'conversion',
        event_label: 'seo_roi_calculated',
        value: Math.round(yearlyIncrease)
      });
    }
  };

  const handleFormSubmit = () => {
    setShowForm(true);
    
    // GA4 Tracking
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'seo_lead_form_started', {
        event_category: 'conversion',
        event_label: 'seo_strategy_consultation'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <TrendingUp className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold text-foreground">SEO ROI Rechner</h1>
          </div>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Berechnen Sie den ROI Ihrer SEO-Investition für Ihr Schweizer Unternehmen
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                ROI Berechnung
              </CardTitle>
              <CardDescription>
                Geben Sie Ihre aktuellen Website-Daten ein
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Monthly Traffic */}
              <div className="space-y-2">
                <Label htmlFor="traffic">Monatliche Website-Besucher</Label>
                <Input
                  id="traffic"
                  type="number"
                  placeholder="z.B. 1000"
                  value={monthlyTraffic}
                  onChange={(e) => setMonthlyTraffic(e.target.value)}
                />
              </div>

              {/* Conversion Rate */}
              <div className="space-y-2">
                <Label htmlFor="cvr">Aktuelle Conversion Rate (%)</Label>
                <Input
                  id="cvr"
                  type="number"
                  step="0.1"
                  placeholder="z.B. 2.5"
                  value={conversionRate}
                  onChange={(e) => setConversionRate(e.target.value)}
                />
                <p className="text-sm text-muted-foreground">
                  Schweizer KMU: 1-3% ist typisch
                </p>
              </div>

              {/* Average Order Value */}
              <div className="space-y-2">
                <Label htmlFor="aov">Durchschnittlicher Auftragswert (CHF)</Label>
                <Input
                  id="aov"
                  type="number"
                  placeholder="z.B. 500"
                  value={averageOrderValue}
                  onChange={(e) => setAverageOrderValue(e.target.value)}
                />
              </div>

              {/* Target Improvement */}
              <div className="space-y-2">
                <Label htmlFor="improvement">Gewünschte Traffic-Steigerung (%)</Label>
                <Select value={targetImprovement} onValueChange={setTargetImprovement}>
                  <SelectTrigger>
                    <SelectValue placeholder="Wählen Sie eine Steigerung" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">+50% (moderat)</SelectItem>
                    <SelectItem value="100">+100% (realistisch)</SelectItem>
                    <SelectItem value="150">+150% (ambitioniert)</SelectItem>
                    <SelectItem value="200">+200% (aggressiv)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={calculateROI} 
                className="w-full"
                disabled={!monthlyTraffic || !averageOrderValue || !targetImprovement}
              >
                <Calculator className="mr-2 h-4 w-4" />
                ROI Berechnen
              </Button>
            </CardContent>
          </Card>

          {/* Results */}
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Ihre SEO ROI Prognose
              </CardTitle>
              <CardDescription>
                Potenzielle Umsatzsteigerung durch SEO
              </CardDescription>
            </CardHeader>
            <CardContent>
              {result ? (
                <div className="space-y-6">
                  {/* ROI Metrics */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold text-primary">
                        {result.monthlyROI.toFixed(0)}%
                      </div>
                      <div className="text-sm text-muted-foreground">Monatlicher ROI</div>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold text-primary">
                        {result.yearlyROI.toFixed(0)}%
                      </div>
                      <div className="text-sm text-muted-foreground">Jährlicher ROI</div>
                    </div>
                  </div>

                  {/* Revenue Projections */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                      <span className="font-medium">Zusätzlicher Monatsumsatz:</span>
                      <span className="text-lg font-bold text-green-600">
                        CHF {result.monthlyRevenue.toLocaleString('de-CH')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                      <span className="font-medium">Zusätzlicher Jahresumsatz:</span>
                      <span className="text-lg font-bold text-blue-600">
                        CHF {result.yearlyRevenue.toLocaleString('de-CH')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                      <span className="font-medium">Geschätzte SEO-Investition:</span>
                      <span className="text-lg font-medium">
                        CHF {result.investment.toLocaleString('de-CH')}
                      </span>
                    </div>
                  </div>

                  {/* Benefits */}
                  <div className="space-y-2">
                    <h4 className="font-semibold">Zusätzliche Vorteile:</h4>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Langfristige organische Sichtbarkeit
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Reduzierte Abhängigkeit von Werbeanzeigen
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Verbessertes Markenvertrauen
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  {!showForm ? (
                    <Button onClick={handleFormSubmit} className="w-full" size="lg">
                      <ArrowRight className="mr-2 h-4 w-4" />
                      Jetzt Strategiegespräch buchen
                    </Button>
                  ) : (
                    <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-semibold">Kostenloses SEO-Strategiegespräch buchen</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <Input placeholder="Vorname" />
                        <Input placeholder="Nachname" />
                      </div>
                      <Input placeholder="E-Mail Adresse" />
                      <Input placeholder="Telefon" />
                      <Input placeholder="Website URL" />
                      <Button className="w-full">
                        Termin vereinbaren
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Füllen Sie das Formular aus, um Ihren SEO ROI zu berechnen</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Swiss Market Info */}
        <Card className="mt-8">
          <CardContent className="p-6">
            <div className="text-center max-w-4xl mx-auto">
              <h3 className="text-xl font-semibold mb-3">
                Warum SEO für Schweizer Unternehmen besonders wertvoll ist
              </h3>
              <div className="grid md:grid-cols-3 gap-6 text-sm">
                <div>
                  <Badge variant="outline" className="mb-2">Hohe Kaufkraft</Badge>
                  <p className="text-muted-foreground">
                    Schweizer Kunden haben eine überdurchschnittliche Kaufkraft und suchen online nach Premium-Lösungen.
                  </p>
                </div>
                <div>
                  <Badge variant="outline" className="mb-2">Weniger Konkurrenz</Badge>
                  <p className="text-muted-foreground">
                    Der lokale Schweizer Markt ist weniger umkämpft als Deutschland oder andere EU-Länder.
                  </p>
                </div>
                <div>
                  <Badge variant="outline" className="mb-2">Langfristige Kunden</Badge>
                  <p className="text-muted-foreground">
                    Schweizer Unternehmen bauen oft langfristige Geschäftsbeziehungen auf, was höhere CLV bedeutet.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SEOROICalculator;